import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useBranding, resolveAppDisplayName } from "../../context/BrandingContext";
import { api, money } from "../../lib/api";
import { checkoutOptions, loadRazorpay } from "../../lib/razorpay";
import { toast } from "sonner";
import CoinsRedeemInput from "../../components/store/CoinsRedeemInput";
import ColorValue from "../../components/store/ColorValue";
import useCheckoutQuote from "../../hooks/useCheckoutQuote";

const errorDetail = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

/** One selectable payment method. `unavailable` greys it out and explains why. */
function PaymentOption({ id, selected, onSelect, mark, title, note, badge, unavailable }) {
  return (
    <label
      className={`flex items-center gap-3 border p-4 transition-colors ${unavailable ? "opacity-60 cursor-not-allowed border-[hsl(var(--kb-border))]" : `cursor-pointer ${selected ? "border-[hsl(var(--kb-primary))] bg-[hsl(var(--kb-surface-alt))]/40" : "border-[hsl(var(--kb-border))]"}`}`}
      data-testid={`pay-method-${id}`}
      data-available={unavailable ? "false" : "true"}
    >
      <input type="radio" name="payment_method" checked={selected} disabled={unavailable}
        onChange={onSelect} className="shrink-0" data-testid={`pay-method-${id}-radio`} />
      {mark}
      <div className="flex-1 min-w-0">
        <div className="font-medium flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>{title}</span>
          {badge}
        </div>
        <div className="text-xs text-[hsl(var(--kb-muted))]" data-testid={`pay-method-${id}-note`}>{note}</div>
      </div>
    </label>
  );
}

export default function Checkout() {
  const { cart, refresh, coupon, setCoupon } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [addr, setAddr] = useState({
    name: user?.name || "", phone: user?.phone || "",
    line1: "", line2: "", city: "", state: "", pincode: "", country: "India",
  });
  const { branding } = useBranding();
  const [processing, setProcessing] = useState(false);
  // What this store can take right now, from the same check `/orders/checkout`
  // enforces — so we never offer a payment the server would then refuse.
  const [methods, setMethods] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null); // "online" | "cod"
  const [coinsRedeemed, setCoinsRedeemed] = useState(0);

  useEffect(() => {
    let alive = true;
    api.get("/orders/payment-methods")
      .then(({ data }) => alive && setMethods(data))
      // Could not ask: offer both and let the server decide at checkout.
      .catch(() => alive && setMethods({ online: { available: true, test_mode: false }, cod: { available: true } }));
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!methods || paymentMethod) return;
    if (methods.online?.available) setPaymentMethod("online");
    else if (methods.cod?.available) setPaymentMethod("cod");
  }, [methods, paymentMethod]);
  const onlineAvailable = !!methods?.online?.available;
  const testMode = onlineAvailable && !!methods?.online?.test_mode;
  const noMethods = !!methods && !onlineAvailable && !methods.cod?.available;

  // A variant line is charged its own variant price (see
  // `_build_order_from_cart`); the cart API resolves it as `unit_price`.
  const unitPrice = (i) => i.unit_price ?? i.product?.price ?? 0;
  const subtotal = (cart.items || []).reduce((s, i) => s + unitPrice(i) * i.qty, 0);
  // Coupon, coins, shipping and total are priced by the server with the
  // same code `/orders/checkout` charges with — never re-derived here.
  const quote = useCheckoutQuote(cart.items, coupon?.code, coinsRedeemed);
  const discount = quote?.discount ?? 0;

  // Group cart items by seller for a preview split in the order summary
  const bySeller = (cart.items || []).reduce((acc, it) => {
    const sid = it.product?.seller_id || "unknown";
    if (!acc[sid]) {
      acc[sid] = {
        seller_id: sid,
        shop_name: it.product?.seller?.shop_name || "Seller",
        items: [],
        subtotal: 0,
      };
    }
    acc[sid].items.push(it);
    acc[sid].subtotal += unitPrice(it) * it.qty;
    return acc;
  }, {});
  const splits = Object.values(bySeller);

  const field = (k) => ({
    value: addr[k],
    onChange: (e) => setAddr({ ...addr, [k]: e.target.value }),
    className: "kb-input",
    "data-testid": `addr-${k}`,
  });

  if (!user) return <div className="px-6 py-24 text-center">Sign in to checkout.</div>;
  if (!cart.items?.length) return <div className="px-6 py-24 text-center">Your cart is empty.</div>;

  const placeOrder = async () => {
    const trimmed = Object.fromEntries(Object.entries(addr).map(([k, v]) => [k, (v || "").toString().trim()]));
    const required = ["name", "phone", "line1", "city", "state", "pincode"];
    for (const k of required) {
      if (!trimmed[k]) { toast.error(`Please enter ${k}`); return; }
    }
    if (!/^\d{10}$/.test(trimmed.phone)) { toast.error("Phone must be 10 digits"); return; }
    if (!/^\d{6}$/.test(trimmed.pincode)) { toast.error("Pincode must be 6 digits"); return; }
    if (trimmed.name.length < 2) { toast.error("Please enter a valid name"); return; }

    if (paymentMethod === "cod") return placeCodOrder(trimmed);
    if (paymentMethod === "online") return payOnline(trimmed);
  };

  const placeCodOrder = async (address) => {
    setProcessing(true);
    try {
      const payload = { address, payment_method: "cod", coins_redeemed: coinsRedeemed };
      if (coupon?.code) payload.coupon_code = coupon.code;
      const { data } = await api.post("/orders/checkout", payload);
      setCoupon(null);
      await refresh();
      toast.success("Order placed — pay when you receive it!");
      navigate(`/order/${data.order.id}/success`);
    } catch (e) {
      toast.error(errorDetail(e, "Checkout failed"));
    } finally {
      setProcessing(false);
    }
  };

  const payOnline = async (address) => {
    setProcessing(true);
    try {
      // The server hands back the SAME open order and payment when nothing
      // changed (refresh, retry, second tab) — so this is always safe to call.
      const payload = { address, payment_method: "online", coins_redeemed: coinsRedeemed };
      if (coupon?.code) payload.coupon_code = coupon.code;
      const { data } = await api.post("/orders/checkout", payload);
      const session = {
        order: data.order, key: data.razorpay_key_id,
        razorpayOrderId: data.razorpay_order_id, amount: data.amount, currency: data.currency,
        expiresAt: Date.now() + (data.payment_window_seconds || 900) * 1000,
      };
      let Razorpay;
      try {
        Razorpay = await loadRazorpay();
      } catch {
        toast.error("Couldn't open the payment window. Check your connection and tap Pay again — you have not been charged.");
        return;
      }
      openRazorpay(Razorpay, session, address);
    } catch (e) {
      if (e?.response?.status === 503) {
        // The store stopped accepting online payments since this page loaded.
        setMethods((m) => ({ ...(m || {}), online: { available: false, test_mode: false } }));
        setPaymentMethod(methods?.cod?.available ? "cod" : null);
      }
      toast.error(errorDetail(e, "Checkout failed"));
    } finally {
      setProcessing(false);
    }
  };

  const openRazorpay = (Razorpay, session, address) => {
    const rzp = new Razorpay(checkoutOptions({
      session,
      address,
      name: resolveAppDisplayName(branding),
      description: `Order ${session.order.order_number || ""}`.trim(),
      themeColor: branding?.theme_color,
      onPaid: (resp) => confirmPayment(session, resp),
      onDismiss: () => toast("Payment not completed — you have not been charged. Tap Pay to try again."),
    }));
    rzp.on("payment.failed", (resp) => {
      const err = resp?.error || {};
      // Razorpay's own reason (e.g. a website-registration block) — useful when
      // a store owner reports "payment failed" from a phone.
      console.warn("[razorpay] payment.failed", { code: err.code, reason: err.reason, source: err.source, step: err.step });
      toast.error(err.description || "Payment failed. You can try again or use another method.");
    });
    rzp.open();
  };

  const confirmPayment = async (session, resp) => {
    setProcessing(true);
    try {
      await api.post("/orders/confirm", {
        order_id: session.order.id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_signature: resp.razorpay_signature,
      });
      setCoupon(null);
      await refresh();
      toast.success("Payment successful!");
      navigate(`/order/${session.order.id}/success`);
    } catch (e) {
      toast.error(e?.response
        ? errorDetail(e, "Payment verification failed")
        : "We couldn't confirm your payment yet. If money was taken, your order will be confirmed automatically — please don't pay again.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-24 py-10" data-testid="checkout-page">
      <div className="overline mb-2">Checkout</div>
      <h1 className="font-display text-4xl lg:text-5xl mb-10">One final step.</h1>

      {/* grid-cols-1 = minmax(0, 1fr): caps the mobile column at the screen
          width so no single-line content can widen the page. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12">
        <div className="space-y-10">
          <section>
            <div className="overline mb-4">Delivery Address</div>
            <div className="grid grid-cols-2 gap-4">
              <input placeholder="Full Name" {...field("name")} />
              <input placeholder="Phone" {...field("phone")} />
              <input placeholder="Address Line 1" className="kb-input col-span-2" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="addr-line1" />
              <input placeholder="Address Line 2 (optional)" className="kb-input col-span-2" value={addr.line2} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} data-testid="addr-line2" />
              <input placeholder="City" {...field("city")} />
              <input placeholder="State" {...field("state")} />
              <input placeholder="Pincode" {...field("pincode")} />
              <input placeholder="Country" {...field("country")} />
            </div>
          </section>

          <section>
            <div className="overline mb-4">Payment</div>
            <div className="space-y-3">
              <PaymentOption
                id="online"
                selected={paymentMethod === "online"}
                onSelect={() => setPaymentMethod("online")}
                unavailable={!!methods && !onlineAvailable}
                mark={<div className="w-10 h-10 bg-[hsl(var(--kb-primary))] text-white flex items-center justify-center font-display text-xl shrink-0">R</div>}
                title="Razorpay · UPI / Cards / Netbanking"
                badge={testMode && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5" data-testid="pay-test-mode">Test mode</span>
                )}
                note={!methods ? "Checking availability…"
                  : !onlineAvailable ? "Online payment isn't available for this store right now."
                    : testMode ? "Razorpay test mode — no real money is charged."
                      : "Pay securely with UPI, cards or netbanking."}
              />
              <PaymentOption
                id="cod"
                selected={paymentMethod === "cod"}
                onSelect={() => setPaymentMethod("cod")}
                unavailable={!!methods && !methods.cod?.available}
                mark={<div className="w-10 h-10 border border-[hsl(var(--kb-primary))] text-[hsl(var(--kb-primary))] flex items-center justify-center font-display text-sm shrink-0">₹</div>}
                title="Cash on Delivery"
                note="Pay when the package arrives at your door."
              />
            </div>
            {noMethods && (
              <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 p-3" role="alert" data-testid="no-payment-methods">
                This store can't take payments right now, so orders can't be placed. Please try again later.
              </div>
            )}
          </section>

          <button className="kb-btn-primary w-full sm:w-auto" onClick={placeOrder}
            disabled={processing || !paymentMethod} data-testid="place-order-btn">
            {processing
              ? "Placing order…"
              : !methods ? "Loading payment options…"
              : !paymentMethod ? "Unavailable"
              : paymentMethod === "cod"
                ? (quote ? `Place Order · ${money(quote.total)}` : "Place Order")
                : (quote ? `Pay ${money(quote.total)}` : "Pay")}
          </button>
        </div>

        <aside className="bg-white border border-[hsl(var(--kb-border))] p-6 h-fit">
          <div className="overline mb-4">Your Order · {splits.length} seller{splits.length === 1 ? "" : "s"}</div>
          <div className="space-y-4 max-h-80 overflow-y-auto" data-testid="order-summary-splits">
            {splits.map((s) => (
              <div key={s.seller_id} className="border-b border-[hsl(var(--kb-border))] pb-3 last:border-b-0" data-testid={`seller-split-${s.seller_id}`}>
                <div className="text-xs uppercase tracking-widest text-[hsl(var(--kb-muted))] mb-2 truncate">
                  {s.shop_name}
                </div>
                {s.items.map((i, idx) => (
                  <div key={idx} className="flex justify-between gap-2 text-sm py-1" data-testid={`checkout-line-${s.seller_id}-${idx}`}>
                    {/* min-w-0 lets a long title wrap inside the column; without
                        it the flex item kept its full width and pushed the whole
                        checkout grid wider than a phone screen. */}
                    <div className="min-w-0">
                      <div className="line-clamp-2 break-words">{i.product?.title}</div>
                      <div className="text-xs text-[hsl(var(--kb-muted))] mt-0.5 flex flex-wrap gap-x-3" data-testid={`checkout-line-variant-${s.seller_id}-${idx}`}>
                        {i.color && <span>Colour: <ColorValue value={i.color} /></span>}
                        {i.size && <span>Size: <b>{i.size}</b></span>}
                        <span>Qty: <b>{i.qty}</b></span>
                      </div>
                    </div>
                    <span className="shrink-0">{money(unitPrice(i) * i.qty)}</span>
                  </div>
                ))}
                <div className="flex justify-between text-xs text-[hsl(var(--kb-primary))] mt-1">
                  <span>Seller subtotal</span><span>{money(s.subtotal)}</span>
                </div>
              </div>
            ))}
          </div>
          <CoinsRedeemInput
            orderSubtotal={subtotal - discount}
            value={coinsRedeemed}
            onChange={setCoinsRedeemed}
          />
          <div className="border-t border-[hsl(var(--kb-border))] mt-4 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span data-testid="checkout-subtotal">{money(quote?.subtotal ?? subtotal)}</span></div>
            {discount > 0 && (
              <div className="flex justify-between text-green-700">
                <span>Coupon ({coupon.code})</span><span data-testid="checkout-discount">−{money(discount)}</span>
              </div>
            )}
            {quote?.coins_discount > 0 && (
              <div className="flex justify-between text-amber-700">
                <span>Coins ({quote.coins_actual})</span>
                <span data-testid="checkout-coins-discount">−{money(quote.coins_discount)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Shipping</span>
              <span data-testid="checkout-shipping">{!quote ? "…" : quote.shipping === 0 ? "Free" : money(quote.shipping)}</span>
            </div>
            <div className="flex justify-between font-display text-lg pt-2 border-t border-[hsl(var(--kb-border))]">
              <span>Total</span><span data-testid="checkout-total">{quote ? money(quote.total) : "…"}</span>
            </div>
          </div>
        </aside>
      </div>

    </div>
  );
}
